import React, { useState, useEffect, useRef, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { Card } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { Table, Empty, Badge, Select, Input, Drawer, Badge as AntBadge, Tooltip } from "antd";
import { SearchOutlined, ReloadOutlined, FilterOutlined, CheckCircleOutlined, TagOutlined, GlobalOutlined, EnvironmentOutlined } from "@ant-design/icons";
import moment from "moment";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import misc from "../../../api/misc/miscService";
import type {
  CouponListItem,
  Country,
  State,
} from "../../../types/super-admin";

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const CouponList: React.FC = () => {
  const navigate = useNavigate();
  const [onSearchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(onSearchText, 300);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    sortOrder: "DESC" as "ASC" | "DESC",
    direction: "P" as "P" | "F" | "B",
    pageDiff: 0,
    paginationId: null as number | null,
  });

  const [dataSource, setDataSource] = useState<CouponListItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [filterDiscountType, setFilterDiscountType] = useState<string | undefined>(undefined);
  const [filterScope, setFilterScope] = useState<string | undefined>(undefined);
  const [filterCountry, setFilterCountry] = useState<number | undefined>(undefined);
  const [filterState, setFilterState] = useState<number | undefined>(undefined);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Dropdown options
  const [countryOptions, setCountryOptions] = useState<Country[]>([]);
  const [stateOptions, setStateOptions] = useState<State[]>([]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await misc.getCountries();
        if (res.status && res.data) {
          setCountryOptions(Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        console.log("Error fetching countries:", error);
      }
    };
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (filterCountry) {
      const fetchStates = async () => {
        try {
          const res = await misc.getStatesByCountry(filterCountry);
          if (res.status && res.data) {
            setStateOptions(Array.isArray(res.data) ? (res.data as State[]) : []);
          }
        } catch (error) {
          console.log("Error fetching states:", error);
        }
      };
      fetchStates();
    } else {
      setStateOptions([]);
      setFilterState(undefined);
    }
  }, [filterCountry]);

  const buildFilters = useCallback(() => {
    const filters: any = {};
    if (filterStatus) filters.status = filterStatus;
    if (filterDiscountType) filters.discountType = filterDiscountType;
    if (filterScope) filters.discountScope = filterScope;
    if (filterCountry) filters.countryId = filterCountry;
    if (filterState) filters.stateId = filterState;
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterStatus, filterDiscountType, filterScope, filterCountry, filterState]);

  const fetchData = async (params: any = {}) => {
    setLoading(true);
    try {
      const filters = params.filters !== undefined ? params.filters : buildFilters();
      const apiParams: any = {
        sortOrder: params.sortOrder || pagination.sortOrder,
        direction: params.direction || pagination.direction,
        pageSize: params.pageSize || pagination.pageSize,
        pageDiff: params.pageDiff ?? pagination.pageDiff,
        paginationId: params.paginationId ?? pagination.paginationId,
        searchQuery:
          params.searchQuery !== undefined
            ? params.searchQuery
            : onSearchText,
      };

      if (filters) {
        if (filters.status) apiParams["filters[status]"] = filters.status;
        if (filters.discountType) apiParams["filters[discountType]"] = filters.discountType;
        if (filters.discountScope) apiParams["filters[discountScope]"] = filters.discountScope;
        if (filters.countryId) apiParams["filters[countryId]"] = filters.countryId;
        if (filters.stateId) apiParams["filters[stateId]"] = filters.stateId;
      }

      const { data, status, message } = await superAdminService.getCouponList(apiParams);

      if (status) {
        const list = Array.isArray(data) ? data : data?.list || [];
        const count = Array.isArray(data) ? data.length : data?.count || 0;
        setDataSource(list);
        setPagination((prev) => ({
          ...prev,
          total: count,
          ...(params.current && { current: params.current }),
          ...(params.pageSize && { pageSize: params.pageSize }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
          ...(params.direction && { direction: params.direction }),
          ...(params.pageDiff !== undefined && { pageDiff: params.pageDiff }),
          ...(params.paginationId !== undefined && {
            paginationId: params.paginationId,
          }),
        }));
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    });
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
      searchQuery: debouncedSearch.trim(),
    });
  }, [debouncedSearch]);

  const tableOnChange = async (
    newPagination: any,
    _filters: any,
    _sorter: any
  ) => {
    const apiParams: any = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    };

    if (newPagination.pageSize !== pagination.pageSize) {
      apiParams.pageDiff = Math.floor(
        ((pagination.current - 1) * pagination.pageSize) /
          newPagination.pageSize
      );
    } else if (newPagination.current !== pagination.current) {
      apiParams.direction =
        newPagination.current > pagination.current ? "F" : "B";
      apiParams.pageDiff =
        Math.abs(newPagination.current - pagination.current) - 1;

      if (apiParams.direction === "F" && dataSource.length > 0) {
        apiParams.paginationId =
          dataSource[dataSource.length - 1]?.paginationId || dataSource[dataSource.length - 1]?.couponId;
      } else if (apiParams.direction === "B" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[0]?.paginationId || dataSource[0]?.couponId;
      }
    }

    await fetchData(apiParams);
  };

  const handleClearFilters = () => {
    setFilterStatus(undefined);
    setFilterDiscountType(undefined);
    setFilterScope(undefined);
    setFilterCountry(undefined);
    setFilterState(undefined);
  };

  const filtersInitialized = useRef(false);

  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    });
  }, [filterStatus, filterDiscountType, filterScope, filterCountry, filterState]);

  const statusColor: Record<string, string> = {
    Active: "success",
    Inactive: "danger",
    Expired: "warning",
  };

  const columns = [
    {
      title: "Coupon Code",
      dataIndex: "couponCode",
      key: "couponCode",
      width: 150,
      render: (text: string, record: CouponListItem) => (
        <Link
          to={`/sa-coupons/${record.couponId}`}
          className="text-primary fw-bold"
        >
          {text || "-"}
        </Link>
      ),
    },
    {
      title: "Coupon Name",
      dataIndex: "couponName",
      key: "couponName",
      width: 200,
    },
    {
      title: "Discount Type",
      dataIndex: "discountType",
      key: "discountType",
      width: 130,
      render: (type: string) => (
        <Badge color={type === "Percent" ? "blue" : "orange"}>{type}</Badge>
      ),
    },
    {
      title: "Discount Value",
      dataIndex: "discountValue",
      key: "discountValue",
      width: 130,
      render: (val: number, record: CouponListItem) =>
        record.discountType === "Percent"
          ? `${record.discountPercent || val}%`
          : Number(val).toFixed(2),
    },
    // NOTE: Scope column hidden for now (App-wise not in use)
    // {
    //   title: "Scope",
    //   dataIndex: "discountScope",
    //   key: "discountScope",
    //   width: 140,
    // },
    {
      title: "Usage Type",
      dataIndex: "usageType",
      key: "usageType",
      width: 120,
    },
    {
      title: "Valid From",
      dataIndex: "validFrom",
      key: "validFrom",
      width: 120,
      render: (date: string) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Valid To",
      dataIndex: "validTo",
      key: "validTo",
      width: 120,
      render: (date: string) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <span className={`tb-status text-${statusColor[status] || "secondary"}`}>
          {status}
        </span>
      ),
    },
    {
      title: "Customers",
      dataIndex: "linkedCustomersCount",
      key: "linkedCustomersCount",
      width: 100,
      render: (val: number) => val || 0,
    },
  ];

  const hasActiveFilters =
    filterStatus || filterDiscountType || filterScope || filterCountry || filterState;

  const activeFilterCount = [filterStatus, filterDiscountType, filterCountry, filterState].filter(Boolean).length;

  return (
    <React.Fragment>
      <Head title="Coupon Management" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Coupon Management
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {pagination.total} coupons.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                onClick={() => navigate("/sa-coupons/create")}
              >
                <Icon name="plus"></Icon>
                <span>Add</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner py-3">
              <div className="d-flex justify-content-between align-items-center">
                <Input
                  prefix={<SearchOutlined style={{ color: "#b0b0b0" }} />}
                  placeholder="Search by coupon code, name..."
                  value={onSearchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  variant="borderless"
                  style={{ width: 570, borderBottom: "1.5px solid #e0e0e0", borderRadius: 0 }}
                  allowClear
                />
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <Tooltip title="Refresh">
                    <span
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", cursor: "pointer", transition: "background 0.2s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      onClick={() => {
                        hasFetched.current = false;
                        fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
                      }}
                    >
                      <ReloadOutlined style={{ fontSize: 16, color: "#555" }} />
                    </span>
                  </Tooltip>
                  <Tooltip title="Filters">
                    <span
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", cursor: "pointer", transition: "background 0.2s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      onClick={() => setFilterDrawerOpen(true)}
                    >
                      <AntBadge count={activeFilterCount} size="small" offset={[-4, 4]}>
                        <FilterOutlined style={{ fontSize: 16, color: "#555" }} />
                      </AntBadge>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="card-inner p-0">
              {loading ? (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                      {["Coupon Code", "Coupon Name", "Discount Type", "Discount Value", "Usage Type", "Valid From", "Valid To", "Status", "Customers"].map((h) => (
                        <th key={h} style={{ padding: "11px 12px", fontSize: 12, fontWeight: 600, color: "#8c8c8c", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(10)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 120, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 160, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 22, borderRadius: 4 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 90, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 85, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 85, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 55, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 40, height: 14 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : dataSource.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  rowKey="couponId"
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '15', '20', '50', '100', '500'],
                  }}
                  onChange={tableOnChange}
                  scroll={{ x: 1500  }}
                  bordered
                  size="small"
                />
              ) : (
                <Empty description="No coupons found" />
              )}
            </div>
          </Card>
        </Block>
      </Content>
      <Drawer
        title={<div style={{ display: "flex", alignItems: "center", gap: 8 }}><FilterOutlined style={{ fontSize: 16, color: "#6576ff" }} /><span style={{ fontSize: 16, fontWeight: 700 }}>Filters</span>{activeFilterCount > 0 && (<AntBadge count={activeFilterCount} style={{ backgroundColor: "#6576ff", marginLeft: 4 }} />)}</div>}
        placement="right" onClose={() => setFilterDrawerOpen(false)} open={filterDrawerOpen} width={450} zIndex={1060}
        extra={hasActiveFilters ? (<span style={{ fontSize: 13, color: "#ff4d4f", cursor: "pointer", fontWeight: 600 }} onClick={handleClearFilters}>Reset All</span>) : null}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)", border: "1px solid #d6e4ff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><CheckCircleOutlined style={{ color: "#1890ff", fontSize: 15 }} /><span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Status</span></div>
            <Select placeholder="All statuses" allowClear style={{ width: "100%" }} value={filterStatus} onChange={(val) => setFilterStatus(val)} options={[{ label: "Active", value: "Active" }, { label: "Inactive", value: "Inactive" }, { label: "Expired", value: "Expired" }]} />
          </div>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)", border: "1px solid #d9f7be" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><TagOutlined style={{ color: "#52c41a", fontSize: 15 }} /><span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Discount Type</span></div>
            <Select placeholder="All types" allowClear style={{ width: "100%" }} value={filterDiscountType} onChange={(val) => setFilterDiscountType(val)} options={[{ label: "Percent", value: "Percent" }, { label: "Flat Amount", value: "Flat Amount" }]} />
          </div>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)", border: "1px solid #d3adf7" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><GlobalOutlined style={{ color: "#722ed1", fontSize: 15 }} /><span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Country</span></div>
            <Select placeholder="All countries" allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} value={filterCountry} onChange={(val) => { setFilterCountry(val); setFilterState(undefined); }} options={countryOptions.map((c) => ({ label: c.countryName, value: c.countryId }))} />
          </div>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)", border: "1px solid #d3adf7" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><EnvironmentOutlined style={{ color: "#722ed1", fontSize: 15 }} /><span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>State</span></div>
            <Select placeholder={filterCountry ? "Select state" : "Select country first"} allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} value={filterState} onChange={(val) => setFilterState(val)} options={stateOptions.map((s) => ({ label: s.stateName, value: s.stateId }))} disabled={!filterCountry} />
          </div>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default CouponList;
